import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Link } from 'react-router-dom';

export default function Receipts({ userRole }) {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Format currency in ZAR
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        let q;
        
        // If admin, fetch all receipts, otherwise only user's receipts
        if (userRole === 'admin') {
          q = query(
            collection(db, 'receipts'),
            orderBy('createdAt', 'desc')
          );
          console.log("Admin: Fetching all receipts");
        } else {
          if (!auth.currentUser) {
            console.error("No authenticated user found when fetching receipts");
            setLoading(false);
            return;
          }
          
          console.log("User: Fetching receipts for user ID:", auth.currentUser.uid);
          
          // Option 1: Use a simpler query that doesn't require a composite index
          q = query(
            collection(db, 'receipts'),
            where('userId', '==', auth.currentUser.uid)
            // Removed the orderBy to avoid needing a composite index
          );
          
          // Option 2: If you still want ordering, you can sort the results in JavaScript
          // after fetching them
        }
        
        const receiptSnapshot = await getDocs(q);
        console.log(`Found ${receiptSnapshot.docs.length} receipts`);
        
        const receiptList = receiptSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // If using Option 2, sort the results here
        if (userRole !== 'admin' && receiptList.length > 0) {
          receiptList.sort((a, b) => {
            // Sort by createdAt in descending order
            const dateA = a.createdAt ? new Date(a.createdAt.seconds * 1000) : new Date(0);
            const dateB = b.createdAt ? new Date(b.createdAt.seconds * 1000) : new Date(0);
            return dateB - dateA;
          });
        }
        
        setReceipts(receiptList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching receipts:', error);
        setLoading(false);
      }
    };

    fetchReceipts();
  }, [userRole]);

  if (loading) return (
    <div className="invoices-container">
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    </div>
  );

  return (
    <div className="invoices-container">
      <div className="invoices-header">
        <h1 className="invoices-title">Payment Receipts</h1>
        <Link 
          to="/create-receipt" 
          className="create-invoice-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
          </svg>
          Create New Receipt
        </Link>
      </div>
      
      {receipts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
              <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z"/>
              <path d="M7 5.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm-1.496-.854a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 0 1 .708-.708l.146.147 1.146-1.147a.5.5 0 0 1 .708 0zM7 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm-1.496-.854a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 0 1 .708-.708l.146.147 1.146-1.147a.5.5 0 0 1 .708 0z"/>
            </svg>
          </div>
          <p className="empty-state-text">No receipts found.</p>
          <Link to="/create-receipt" className="empty-state-btn">Create your first receipt</Link>
        </div>
      ) : (
        <div className="invoices-table-container">
          <table className="invoices-table">
            <thead>
              <tr>
                <th>Receipt #</th>
                <th>Client</th>
                <th>Amount</th>
                <th>Payment Date</th>
                <th>Payment Method</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {receipts.map(receipt => (
                <tr key={receipt.id}>
                  <td data-label="Receipt #">
                    <span className="invoice-id">{receipt.receiptNumber}</span>
                  </td>
                  <td data-label="Client">{receipt.clientName}</td>
                  <td data-label="Amount">
                    <span className="invoice-amount">{formatCurrency(receipt.amount)}</span>
                  </td>
                  <td data-label="Payment Date">
                    {receipt.paymentDate && receipt.paymentDate.toDate ? 
                      receipt.paymentDate.toDate().toLocaleDateString() : 
                      receipt.paymentDate ? new Date(receipt.paymentDate).toLocaleDateString() : 'N/A'}
                  </td>
                  <td data-label="Method">
                    <span className="payment-method">
                      {receipt.paymentMethod === 'cash' ? 'Cash' : 
                       receipt.paymentMethod === 'bank_transfer' ? 'Bank Transfer' :
                       receipt.paymentMethod === 'credit_card' ? 'Credit Card' :
                       receipt.paymentMethod === 'check' ? 'Check' : 'Other'}
                    </span>
                  </td>
                  <td className="responsive-actions-cell" data-label="Actions">
                    <div className="invoice-actions">
                      <Link 
                        to={`/receipt/${receipt.id}`}
                        className="action-btn view-btn"
                      >
                        View
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
